import React from 'react'
import PropTypes from 'prop-types'

// Components
import {
    Box,
    Button,
    Divider,
    Heading,
    Text,
    VStack
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage} from 'react-intl'
import Field from '@app/components/field'
import FieldsetForm from '@app/components/shared/ui/FieldsetForm'
import {getDefaultShipmentID} from '../../../utils/basket-utils'

// Hooks
import {useCurrentBasket} from '@app/hooks/use-current-basket'
import {useForm} from 'react-hook-form'
import {useGiftCardActions} from '@app/hooks/use-gift-card-actions'
import {useGiftCardLogic} from '@app/hooks/use-gift-card-logic'
import {useIntl} from 'react-intl'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useToast} from '@app/hooks/use-toast'
import useBuyGiftCardFields from '@app/components/forms/useBuyGiftCardFields'

export const BuyGiftCardForm = ({onSuccess}) => {
    const styles = useStyleConfig('Giftcard')
    const toast = useToast()
    const intl = useIntl()
    const {data: basket, refetch: refetchBasket} = useCurrentBasket()
    const applyGiftCertificateMutation = useShopperBasketsMutation('addGiftCertificateItemToBasket')
    const createBasketMutation = useShopperBasketsMutation('createBasket')
    const {getGiftcardPaymentInstruments} = useGiftCardLogic()
    const {removeAllGiftCardPaymentInstruments} = useGiftCardActions(basket)
    const DEFAULT_SHIPMENT_ID = getDefaultShipmentID(basket)

    const form = useForm({mode: 'all'})
    const {
        handleSubmit,
        reset,
        watch,
        formState: {isSubmitting}
    } = form
    const formFields = useBuyGiftCardFields({form, prefix: 'giftcard_'})

    /**
     * Adds a gift card to the shopping cart and handles the success or error response.
     *
     * @param {Object} data - The gift card data to be added to the cart.
     * @param {Object} basket - The current shopping basket object, which may contain a basketId.
     * @param {string} [basket.basketId] - The unique identifier for the basket.
     * @returns {Promise<void>}
     *
     * @async
     */
    async function addCardToCart(data, basket) {
        const isUsingGiftcardAsPayment = getGiftcardPaymentInstruments(basket).length > 0

        try {
            if (isUsingGiftcardAsPayment) {
                // Remove GCs from payment instruments, because GCs cannot be paid for with other GCs
                await removeAllGiftCardPaymentInstruments()
                toast({
                    title: intl.formatMessage({
                        id: 'giftcard.toast.removed_all_applied_codes.message',
                        defaultMessage: 'Your applied Gift Card codes have been removed'
                    }),
                    description: intl.formatMessage({
                        id: 'giftcard.toast.removed_all_applied_codes.description',
                        defaultMessage:
                            'You cannot pay with Gift Card codes when purchasing Gift Cards'
                    })
                })
            }
        } catch {
            console.error(
                '[addCardToCart] Failed to remove applied GC codes before adding GC line item to cart'
            )
        }

        await applyGiftCertificateMutation.mutateAsync(
            {
                parameters: {
                    basketId: basket?.basketId
                },
                body: data
            },
            {
                onSuccess: async () => {
                    await refetchBasket()
                    onSuccess(data)
                    reset()
                },
                onError: () => {
                    toast({
                        title: intl.formatMessage({
                            id: 'giftcard.toast.error',
                            defaultMessage:
                                'Something went wrong while adding your online gift card to the cart'
                        }),
                        variant: 'error'
                    })
                }
            }
        )
    }

    /**
     * Handles the submission of the gift card form by preparing the data and adding the gift card to the cart.
     * @async
     *
     * @param {Object} values - The form values containing gift card details.
     * @param {string} values.predefinedAmount - The amount for the gift card. Selected from the predefined amounts.
     * @param {string} values.customAmount - The amount for the gift card. Written by hand.
     * @param {string} values.friendEmail - The recipient's email address.
     * @param {string} values.yourName - The sender's name.
     * @param {string} values.friendName - The recipient's name.
     * @param {string} values.message - A personal message to include with the gift card.
     * @param {boolean} [values.checkbox] - Indicates if a special send date is selected.
     * @param {string} [values.selectDay] - The day of the special send date.
     * @param {string} [values.selectMonth] - The month of the special send date.
     * @param {string} [values.selectYear] - The year of the special send date.
     *
     * @returns {Promise<void>}
     *
     * @description
     * This function constructs a data object from the provided form values, including the gift card amount, recipient and sender details, and an optional special send date.
     * If a basket does not exist, it triggers the creation of a new basket and adds the gift card to it upon successful creation.
     * If a basket already exists, it directly adds the gift card to the existing basket.
     */
    async function onSubmit(values) {
        const data = {
            amount: parseInt(
                values[formFields.predefinedAmount.name] || values[formFields.customAmount.name]
            ),
            recipientEmail: values[formFields.friendEmail.name],
            senderName: values[formFields.yourName.name],
            recipientName: values[formFields.friendName.name],
            message: values[formFields.message.name],
            shipmentId: DEFAULT_SHIPMENT_ID
        }

        if (values[formFields.checkbox.name] && values[formFields.sendDate.name]) {
            data.c_specialSendDate = new Date(values[formFields.sendDate.name]).toISOString()
        }

        if (!basket?.basketId) {
            const newBasket = await createBasketMutation.mutateAsync({body: {}})
            await addCardToCart(data, newBasket)
        } else {
            await addCardToCart(data, basket)
        }
    }

    return (
        <FieldsetForm
            id="giftcard-buy-form"
            data-testid="sf-check-giftcard-buy-form"
            isMandatoryNote={false}
            legendText={intl.formatMessage({
                id: 'giftcard.form.buy.legend',
                defaultMessage: 'Buy gift card form'
            })}
            onSubmit={handleSubmit(onSubmit)}
        >
            <Heading size="4xl" textTransform="uppercase" mb={16}>
                <FormattedMessage
                    id="giftcard.form.buy.title"
                    defaultMessage="Offer an online card"
                />
            </Heading>
            <Box>
                <Text variant="bodyBase2" {...styles.number}>
                    1
                </Text>
                <Text variant="eyeBrowSmall" textTransform="uppercase" mb={8}>
                    <FormattedMessage
                        id="giftcard.form.buy.subtitle1"
                        defaultMessage="Choose your amount:"
                    />
                </Text>
            </Box>

            <Field {...formFields.predefinedAmount} />
            <Field {...formFields.customAmount} />
            <Divider borderColor="borderGray" my={10} />

            <Box>
                <Text variant="bodyBase2" {...styles.number}>
                    2
                </Text>
                <Text variant="eyeBrowSmall" textTransform="uppercase" mb={16}>
                    <FormattedMessage
                        id="giftcard.form.buy.subtitle2"
                        defaultMessage="Enter details:"
                    />
                </Text>
            </Box>
            <Text variant="bodyBase2" mb={24}>
                <FormattedMessage
                    id="giftcard.form.buy.text"
                    defaultMessage="If you wish to offer your online gift card in person, create it in your own name. You can personalise it and print it on receipt of the email."
                />
            </Text>

            <Field {...formFields.yourName} />
            <Field {...formFields.friendName} />
            <Field {...formFields.friendEmail} />
            <Field {...formFields.confirmFriendEmail} />
            <Field {...formFields.message} />

            <VStack spacing={24}>
                <Field {...formFields.checkbox} />
                {watch(formFields.checkbox.name) && <Field {...formFields.sendDate} />}
            </VStack>

            <Button type="submit" isLoading={isSubmitting} variant="primary" width="full">
                <FormattedMessage id="giftcard.form.buy.button" defaultMessage="Add to cart" />
            </Button>
        </FieldsetForm>
    )
}

BuyGiftCardForm.propTypes = {
    onSuccess: PropTypes.func
}
